import * as React from 'react';
import cx from 'classnames';
import { EditorState } from 'draft-js';
import { Tooltip } from 'antd';
import { ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';

interface IUndoRedoControlsProps {
  editorState: EditorState;
  onUndo(): void;
  onRedo(): void;
  className?: string;
}

export const UndoRedoControls: React.FC<IUndoRedoControlsProps> = React.memo((props) => {
  const { editorState } = props;

  const canUndo = editorState.getUndoStack().size > 0;
  const canRedo = editorState.getRedoStack().size > 0;

  const handleUndo = (event: React.MouseEvent) => {
    event.preventDefault();
    if (canUndo) {
      props.onUndo();
    }
  };

  const handleRedo = (event: React.MouseEvent) => {
    event.preventDefault();
    if (canRedo) {
      props.onRedo();
    }
  };

  return (
    <div className={cx('flex items-center gap-1', props.className)}>
      <Tooltip
        title={(
          <div className="flex items-center gap-2 text-xs">
            Undo
            <span className="bg-white bg-opacity-20 px-1.5 py-0.5 rounded text-xs font-mono">
              ⌘Z
            </span>
          </div>
        )}
      >
        <div
          className={cx(
            'flex items-center justify-center w-8 h-8 rounded transition-colors',
            canUndo
              ? 'cursor-pointer hover:bg-slate-100 active:bg-slate-200'
              : 'cursor-not-allowed'
          )}
          onMouseDown={handleUndo}
        >
          <ArrowUturnLeftIcon className={cx(
            'w-4 h-4',
            canUndo ? 'text-slate-500 hover:text-slate-700' : 'text-slate-300'
          )}
          />
        </div>
      </Tooltip>

      <Tooltip
        title={(
          <div className="flex items-center gap-2 text-xs">
            Redo
            <span className="bg-white bg-opacity-20 px-1.5 py-0.5 rounded text-xs font-mono">
              ⌘Y
            </span>
          </div>
        )}
      >
        <div
          className={cx(
            'flex items-center justify-center w-8 h-8 rounded transition-colors',
            canRedo
              ? 'cursor-pointer hover:bg-slate-100 active:bg-slate-200'
              : 'cursor-not-allowed'
          )}
          onMouseDown={handleRedo}
        >
          <ArrowUturnRightIcon className={cx(
            'w-4 h-4',
            canRedo ? 'text-slate-500 hover:text-slate-700' : 'text-slate-300'
          )}
          />
        </div>
      </Tooltip>
    </div>
  );
});

UndoRedoControls.displayName = 'UndoRedoControls';
